import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Card, CardBody, Tooltip } from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { Settlement } from 'common/src/utils/types';
import { getWeekKey, sortWeekKeys } from 'common/src/utils/functions';
import { formatMoneyUSD, formatNumber } from '../../utils/functions';
import { formatXAxisLabel } from '../specifics/settlements-client-comparision/ShipmentBarChart';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { SUMMARY_ROW_CLASS, TableExportRef, TH_BASE_CLASS } from '../../utils/table-styles';
import { exportTableToExcel } from '../../utils/export-to-excel';

interface Props {
  settlements: Settlement[];
  dateType: 'eta' | 'etd' | 'sale_date';
}

type SortDirection = 'ascending' | 'descending';

interface WeekVarietyFOB {
  variety: string;
  weekKey: string;
  totalKilos: number;
  totalFob: number;
  detailCount: number;
}

interface WeekSummary {
  totalKilos: number;
  totalFob: number;
  detailCount: number;
}

interface MatrixMetric {
  value: number;
  totalKilos: number;
  totalFob: number;
  detailCount: number;
}

const metricFromSummary = (summary?: WeekSummary): MatrixMetric | null => {
  if (!summary || summary.totalKilos <= 0) return null;

  return {
    value: summary.totalFob / summary.totalKilos,
    totalKilos: summary.totalKilos,
    totalFob: summary.totalFob,
    detailCount: summary.detailCount,
  };
};

const metricFromGroup = (group?: WeekVarietyFOB): MatrixMetric | null => {
  if (!group || group.totalKilos <= 0) return null;

  return {
    value: group.totalFob / group.totalKilos,
    totalKilos: group.totalKilos,
    totalFob: group.totalFob,
    detailCount: group.detailCount,
  };
};

const metricValue = (metric: MatrixMetric | null) =>
  metric ? formatMoneyUSD(metric.value, { decimals: 1 }) : '-';

const FOBAccumulatedWeekVariety = forwardRef<TableExportRef, Props>(
  ({ settlements, dateType }, ref) => {
    const { t } = useTranslation('settlements');
    const [sortState, setSortState] = useState<{
      column: string;
      direction: SortDirection;
    }>({
      column: '',
      direction: 'descending',
    });

    const totalLabel = t('Acumulado Total');

    const fobData = useMemo(() => {
      const groups: Record<string, WeekVarietyFOB> = {};
      const weekTotals: Record<string, WeekSummary> = {};
      const varietyTotals: Record<string, WeekSummary> = {};
      const weeks = new Set<string>();

      settlements.forEach((settlement) => {
        const dateValue =
          dateType === 'eta'
            ? settlement.ship_trip?.eta
            : dateType === 'etd'
              ? settlement.ship_trip?.etd
              : settlement.date_sold;

        if (!dateValue) return;

        const weekKey = `WEEK-${getWeekKey(dateValue)}`;
        weeks.add(weekKey);

        if (!weekTotals[weekKey]) {
          weekTotals[weekKey] = {
            totalKilos: 0,
            totalFob: 0,
            detailCount: 0,
          };
        }

        settlement.settlement_details?.forEach((detail) => {
          const variety = detail.variety || t('Sin Variedad');
          const key = `${variety}-${weekKey}`;

          if (!groups[key]) {
            groups[key] = {
              variety,
              weekKey,
              totalKilos: 0,
              totalFob: 0,
              detailCount: 0,
            };
          }

          if (!varietyTotals[variety]) {
            varietyTotals[variety] = {
              totalKilos: 0,
              totalFob: 0,
              detailCount: 0,
            };
          }

          const kilos = detail.kilos || 0;
          const fob = detail.usd_total_fob || 0;

          groups[key].totalKilos += kilos;
          groups[key].totalFob += fob;
          groups[key].detailCount += 1;

          weekTotals[weekKey].totalKilos += kilos;
          weekTotals[weekKey].totalFob += fob;
          weekTotals[weekKey].detailCount += 1;

          varietyTotals[variety].totalKilos += kilos;
          varietyTotals[variety].totalFob += fob;
          varietyTotals[variety].detailCount += 1;
        });
      });

      return {
        groups,
        weeks: Array.from(weeks).sort(sortWeekKeys),
        varieties: Object.keys(varietyTotals).sort(),
        weekTotals,
        varietyTotals,
      };
    }, [settlements, dateType, t]);

    const getCellMetric = (
      variety: string,
      weekKey: string
    ): MatrixMetric | null => {
      if (variety === totalLabel) {
        return metricFromSummary(fobData.weekTotals[weekKey]);
      }

      return metricFromGroup(fobData.groups[`${variety}-${weekKey}`]);
    };

    const rows = useMemo(
      () => [totalLabel, ...fobData.varieties],
      [fobData.varieties, totalLabel]
    );

    const sortedRows = useMemo(() => {
      const [summaryRow, ...dataRows] = rows;
      if (!sortState.column) return rows;

      if (sortState.column === 'variety') {
        const sorted = [...dataRows].sort((a, b) =>
          sortState.direction === 'ascending'
            ? a.localeCompare(b)
            : b.localeCompare(a)
        );
        return [summaryRow, ...sorted];
      }

      const sorted = [...dataRows].sort((a, b) => {
        const metricA = getCellMetric(a, sortState.column);
        const metricB = getCellMetric(b, sortState.column);

        if (!metricA && !metricB) return a.localeCompare(b);
        if (!metricA) return 1;
        if (!metricB) return -1;

        return sortState.direction === 'ascending'
          ? metricA.value - metricB.value
          : metricB.value - metricA.value;
      });

      return [summaryRow, ...sorted];
    }, [rows, sortState, fobData]);

    const heatRange = useMemo(() => {
      const values = rows.flatMap((row) =>
        fobData.weeks
          .map((week) => getCellMetric(row, week)?.value)
          .filter((value): value is number => typeof value === 'number')
      );

      return {
        min: Math.min(...values),
        max: Math.max(...values),
      };
    }, [rows, fobData]);

    const getHeatStyle = (metric: MatrixMetric | null): React.CSSProperties => {
      if (!metric || !isFinite(heatRange.min) || !isFinite(heatRange.max)) {
        return {};
      }

      const range = heatRange.max - heatRange.min;
      const ratio = range > 0 ? (metric.value - heatRange.min) / range : 0.5;
      const alpha = 0.08 + ratio * 0.28;

      return {
        backgroundColor: `rgba(16, 185, 129, ${alpha})`,
      };
    };

    const handleSort = (column: string) => {
      setSortState((current) => ({
        column,
        direction:
          current.column === column && current.direction === 'descending'
            ? 'ascending'
            : 'descending',
      }));
    };

    const SortIndicator = ({ column }: { column: string }) => {
      if (sortState.column !== column) {
        return (
          <Icon
            icon='tabler:chevron-down'
            className='inline ml-1 opacity-30'
            width={10}
          />
        );
      }
      return sortState.direction === 'ascending' ? (
        <Icon icon='tabler:chevron-up' className='inline ml-1' width={10} />
      ) : (
        <Icon icon='tabler:chevron-down' className='inline ml-1' width={10} />
      );
    };

    const renderTooltip = (metric: MatrixMetric) => (
      <div className='space-y-1 text-xs'>
        <div>
          {t('FOB promedio')}: {formatMoneyUSD(metric.value, { decimals: 2 })}
        </div>
        <div>
          {t('Kilos')}: {formatNumber(metric.totalKilos, { decimals: 0 })}
        </div>
        <div>
          {t('FOB total')}: {formatMoneyUSD(metric.totalFob, { decimals: 0 })}
        </div>
        <div>
          {t('Detalles')}: {formatNumber(metric.detailCount, { decimals: 0 })}
        </div>
      </div>
    );

    const renderMetricCell = (
      row: string,
      week: string,
      isSummary: boolean
    ) => {
      const metric = getCellMetric(row, week);

      if (!metric) {
        return (
          <td key={week} className='py-2 px-3 text-right text-default-300'>
            -
          </td>
        );
      }

      return (
        <td key={week} className='py-1.5 px-2 text-right'>
          <Tooltip content={renderTooltip(metric)} placement='top'>
            <span
              className={`inline-flex w-full min-w-[76px] justify-end rounded-md px-2 py-1 tabular-nums ${
                isSummary ? 'font-semibold' : ''
              }`}
              style={getHeatStyle(metric)}
            >
              {metricValue(metric)}
            </span>
          </Tooltip>
        </td>
      );
    };

    const handleExport = () => {
      const headers = [
        t('Variedad'),
        ...fobData.weeks.map((week) =>
          week.includes('-') ? formatXAxisLabel(week, 'weeks') : week
        ),
      ];

      const exportRows = rows.map((row) => [
        row,
        ...fobData.weeks.map((week) => metricValue(getCellMetric(row, week))),
      ]);

      exportTableToExcel({
        fileName: 'fob-acumulado-semana-variedad',
        sheetName: 'FOB Semana Variedad',
        headers,
        rows: exportRows,
      });
    };

    useImperativeHandle(ref, () => ({
      exportToExcel: handleExport,
    }));

    return (
      <Card shadow='sm' className='w-full'>
        <CardBody className='p-0'>
          <div className='max-h-[620px] overflow-auto'>
            <table
              className='w-full border-separate border-spacing-0 text-sm'
              style={{ minWidth: `${Math.max(760, 130 + fobData.weeks.length * 92)}px` }}
            >
              <thead className='sticky top-0 z-20'>
                <tr>
                  <th
                    className={`${TH_BASE_CLASS} sticky left-0 z-30 bg-default-100 text-left`}
                    onClick={() => handleSort('variety')}
                  >
                    {t('VARIEDAD')}
                    <SortIndicator column='variety' />
                  </th>
                  {fobData.weeks.map((week) => (
                    <th
                      key={week}
                      className={`${TH_BASE_CLASS} bg-default-100 text-right`}
                      onClick={() => handleSort(week)}
                    >
                      {week.includes('-') ? formatXAxisLabel(week, 'weeks') : week}
                      <SortIndicator column={week} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => {
                  const isSummary = row === totalLabel;
                  return (
                    <tr
                      key={row}
                      className={
                        isSummary
                          ? SUMMARY_ROW_CLASS
                          : 'border-b border-default-100 hover:bg-default-50'
                      }
                    >
                      <td className='sticky left-0 z-10 bg-background py-2 px-3 font-medium'>
                        {row}
                      </td>
                      {fobData.weeks.map((week) =>
                        renderMetricCell(row, week, isSummary)
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    );
  }
);

FOBAccumulatedWeekVariety.displayName = 'FOBAccumulatedWeekVariety';

export default React.memo(FOBAccumulatedWeekVariety);
